import React, { FC } from 'react';
import './Widget.css';
import Alert from '../Alert/Alert';
import { ActionStatusHelper, MessageHelper } from '@/Shared/Helpers';
import PageLoader from '../PageLoader/PageLoader';
import { ActionResponse, FeedBackType } from '@/Shared/Types';

interface WidgetProps {
	domainLabel: string;
	title?: string;
	content?: JSX.Element;
	classes?: string;
	response?: ActionResponse;
}

/**
 * Widget
 *
 * Generic layout container used to wrap domain content
 * with consistent UI behavior (loading, error, title).
 *
 * This component acts as a visual shell:
 * - It does NOT fetch data
 * - It relies on an `ActionResponse` to infer UI state
 *
 * Responsibilities:
 * - Display a contextual title when provided
 * - Handle loading and error states in a unified way
 * - Provide a consistent layout for embedded content
 *
 * Typical use cases:
 * - Domain widgets
 * - Dashboard blocks
 * - Stack or card-based containers
 * 
 * Author: Samano CASTRE
 * Date: January 2026
 */
const Widget: FC<WidgetProps> = ({
	title,
	content,
	classes,
	response = {} as ActionResponse,
	domainLabel
}) => {

	/**
	 * Resolve the current UI state from the action response
	 */
	const { isLoading, isFailed } = ActionStatusHelper.is(response.status);

	return (
		<div className={`Widget ${classes ? classes : ''}`} data-testid="Widget">

			{/* Optional widget title */}
			{title && (
				<h4 className="widget-title">
					{title}
				</h4>
			)}

			<div className="widget-body">

				{/* Error state */}
				{isFailed && (
					<Alert
						data={{
							message: response.error || 'Une erreur est survenue',
							type: FeedBackType.ERROR
						}}
					/>
				)}

				{/* Loading state or missing content */}
				{(isLoading || !content) && (
					<PageLoader
						message={MessageHelper.FETCHING.LOADING(domainLabel)}
					/>
				)}

				{/* Render widget content */}
				{content}
			</div>
		</div>
	);
};

export default Widget;
